import { proxy } from 'valtio';
import {
  CourseKindSchema,
  type CourseKind,
} from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import {
  WorkSchedule,
  CourseSegment,
  SkillSetContractCategories,
} from '@duke-hq/shared-constants/enrolments';
import { djs } from '@duke-hq/djs';

export type SelectedMemberType = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
};

export type InternalEnrolmentEnrolmentSettingsType = {
  contract_category: SkillSetContractCategories;
  enrolment_contract_type: string;
  course_segment: CourseSegment;
  external_id: string;
  start_at_date: string; // must be in UTC 'YYYY-MM-DD' format - don't use Date type here
  semester_2_start_at_date: string | null; // must be in UTC 'YYYY-MM-DD' format - don't use Date type here
  work_schedule: WorkSchedule;
  site_location: {
    site_id: string;
    site_name: string;
    site_state: string;
  };
};

export const internalEnrolmentState = proxy({
  mode: 'CREATE_NEW',
  selectedUserAttributes: {
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    role: '',
  },
  selectedParticipant: {
    id: '',
  },
  selectedCourseAttributes: {
    id: '',
    identifier: '',
    price_aud: 0,
    specialisation: '',
    title: '',
    kind: 'Skill set',
  },
  enrolmentSettingsData: {
    contract_category: SkillSetContractCategories.INTERNAL_ENROLMENTS,
    enrolment_contract_type: 'Other',
    course_segment: CourseSegment.FULL_COURSE,
    external_id: '',
    start_at_date: djs().format('YYYY-MM-DD'),
    work_schedule: WorkSchedule.FULL_TIME,
    site_location: { site_id: '', site_name: '', site_state: '' },
  },
  isModalOpen: false,
  memberForEnrolment: {
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    role: '',
  },
  setSelectedUserAttributes: ({
    id,
    first_name,
    last_name,
    email,
    role,
  }: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
  }) => {
    internalEnrolmentState.selectedUserAttributes = {
      id,
      first_name,
      last_name,
      email,
      role,
    };
  },
  setSelectedCourseAttributes: ({
    id,
    identifier,
    price_aud,
    specialisation,
    title,
    kind,
  }: {
    id: string;
    identifier: string;
    price_aud: number;
    specialisation: string;
    title: string;
    kind: CourseKind;
  }) => {
    internalEnrolmentState.selectedCourseAttributes = {
      id,
      identifier,
      price_aud,
      specialisation,
      title,
      kind,
    };
  },
  setEnrolmentSettingsData: ({
    contract_category,
    enrolment_contract_type,
    course_segment,
    external_id,
    start_at_date,
    work_schedule,
    site_location,
  }: InternalEnrolmentEnrolmentSettingsType) => {
    internalEnrolmentState.enrolmentSettingsData = {
      contract_category,
      enrolment_contract_type,
      course_segment,
      external_id,
      start_at_date,
      work_schedule,
      site_location,
    };
  },
  setSelectedParticipant: ({ id }: { id: string }) => {
    internalEnrolmentState.selectedParticipant = {
      id,
    };
  },

  openModal: (member?: SelectedMemberType) => {
    internalEnrolmentState.isModalOpen = true;
    if (member?.id != null) {
      internalEnrolmentState.memberForEnrolment = member;
    }
  },
  closeModal: () => {
    internalEnrolmentState.isModalOpen = false;
    internalEnrolmentState.resetStates();
    internalEnrolmentState.memberForEnrolment = {
      id: '',
      first_name: '',
      last_name: '',
      email: '',
      role: '',
    };
  },
  resetStates: () => {
    internalEnrolmentState.selectedUserAttributes = {
      id: '',
      first_name: '',
      last_name: '',
      email: '',
      role: '',
    };
    internalEnrolmentState.selectedCourseAttributes = {
      id: '',
      identifier: '',
      title: '',
      specialisation: '',
      price_aud: 0,
      kind: CourseKindSchema.enum.SKILL_SET,
    };
    internalEnrolmentState.enrolmentSettingsData = {
      contract_category: SkillSetContractCategories.INTERNAL_ENROLMENTS,
      enrolment_contract_type: 'Other',
      course_segment: CourseSegment.FULL_COURSE,
      external_id: '',
      start_at_date: djs().format('YYYY-MM-DD'),
      work_schedule: WorkSchedule.FULL_TIME,
      site_location: { site_id: '', site_name: '', site_state: '' },
    };
    internalEnrolmentState.selectedParticipant = {
      id: '',
    };
    internalEnrolmentState.isModalOpen = false;
    internalEnrolmentState.mode = 'CREATE_NEW';
  },
});
